import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Stethoscope, Menu, X, Type, Globe } from "lucide-react";
import { useT, useLang, SUPPORTED } from "../lib/i18n";

const SHORT = { en: "EN", es: "ES", zh: "中文", vi: "VI" };

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [large, setLarge] = useState(
    () => localStorage.getItem("amd-large-text") === "1"
  );
  const navigate = useNavigate();
  const t = useT();
  const { lang, setLang } = useLang();

  useEffect(() => {
    document.documentElement.classList.toggle("large-text", large);
    localStorage.setItem("amd-large-text", large ? "1" : "0");
  }, [large]);

  const links = [
    { to: "/wellness", key: "wellness", label: t("nav_wellness") },
    { to: "/tools", key: "tools", label: t("nav_tools") },
    { to: "/library", key: "health-library", label: t("nav_library") },
    { to: "/admin", key: "dashboard", label: t("nav_dashboard") },
    { to: "/disclaimer", key: "disclaimer", label: t("nav_disclaimer") },
  ];

  const LangToggle = ({ mobile }) => (
    <div
      className={`flex items-center rounded-lg border border-slate-200 overflow-hidden ${mobile ? "w-full" : ""}`}
      data-testid="language-switcher"
    >
      {!mobile && <Globe className="w-4 h-4 ml-2 text-slate-400" />}
      {SUPPORTED.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          aria-pressed={lang === l}
          className={`flex items-center justify-center px-2.5 py-2.5 text-sm font-semibold transition-colors ${mobile ? "flex-1" : ""} ${
            lang === l ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-teal-50"
          }`}
        >
          {SHORT[l]}
        </button>
      ))}
    </div>
  );

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-2.5 group"
          onClick={() => setOpen(false)}
        >
          <span className="grid place-items-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-teal-600 text-white shadow-sm group-hover:bg-teal-700 transition-colors">
            <Stethoscope className="w-6 h-6" />
          </span>
          <span className="font-heading font-extrabold text-lg sm:text-xl text-slate-900 tracking-tight">
            AskMyDoctor <span className="text-teal-600">AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className="px-4 py-2.5 rounded-lg text-base font-semibold text-slate-700 hover:text-teal-700 hover:bg-teal-50 transition-colors"
            >
              {l.label}
            </Link>
          ))}
          <div className="ml-2"><LangToggle /></div>
          <button
            data-testid="large-text-toggle"
            onClick={() => setLarge((v) => !v)}
            aria-pressed={large}
            className={`ml-2 flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-semibold transition-colors ${
              large
                ? "bg-teal-600 text-white"
                : "text-slate-700 hover:bg-teal-50 border border-slate-200"
            }`}
          >
            <Type className="w-4 h-4" /> {t("large_text")}
          </button>
        </nav>

        <button
          className="md:hidden p-2 text-slate-700"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
          {links.map((l) => (
            <button
              key={l.to}
              onClick={() => {
                navigate(l.to);
                setOpen(false);
              }}
              data-testid={`mobile-nav-${l.key}`}
              className="w-full text-left px-4 py-3 rounded-lg text-lg font-semibold text-slate-700 hover:bg-teal-50"
            >
              {l.label}
            </button>
          ))}
          <div className="pt-2"><LangToggle mobile /></div>
          <button
            onClick={() => setLarge((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 rounded-lg text-lg font-semibold text-slate-700 hover:bg-teal-50"
          >
            <Type className="w-5 h-5" /> {large ? t("normal_text") : t("large_text")}
          </button>
        </div>
      )}
    </header>
  );
};
